/**
 * quoteTotals — Única fuente de verdad para los importes de una cotización.
 *
 * Los totales SIEMPRE se derivan de las líneas (productos) de la cotización:
 *
 *   Subtotal = Σ (cantidad × precio de venta)
 *   IVA      = Σ IVA por línea (según el % configurado en el producto)
 *   Total    = Subtotal + IVA
 *
 * Los campos `total_quote` / `iva_quote` guardados en la cotización se tratan
 * solo como caché: se recalculan y se persisten cuando cambian las líneas.
 */

import type { QuoteProduct } from "../types";

export interface QuoteTotals {
  subtotal: number;
  iva: number;
  total: number;
}

/** Redondeo monetario a 2 decimales, evitando el error binario de coma flotante. */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const lineQuantity = (product: QuoteProduct): number => toNumber(product.product_amount);

/** Precio unitario: se prioriza el precio de venta capturado en la cotización. */
export const lineUnitPrice = (product: QuoteProduct): number =>
  toNumber(product.product_price_sale ?? product.product_price);

export const lineSubtotal = (product: QuoteProduct): number =>
  round2(lineQuantity(product) * lineUnitPrice(product));

/**
 * IVA de una línea.
 * - Si el producto tiene porcentaje (`product_iva_percentage`) → importe × %.
 * - Si no, pero tiene un IVA fijo por unidad (`product_iva`) → IVA × cantidad.
 * - Si no tiene ninguno ("Sin IVA") → 0.
 */
export const lineIva = (product: QuoteProduct): number => {
  const percentage = toNumber(product.product_iva_percentage);
  if (percentage > 0) return round2((lineSubtotal(product) * percentage) / 100);

  const fixedIva = toNumber(product.product_iva);
  if (fixedIva > 0) return round2(fixedIva * lineQuantity(product));

  return 0;
};

export const lineTotal = (product: QuoteProduct): number =>
  round2(lineSubtotal(product) + lineIva(product));

/** Totales de la cotización calculados a partir de sus líneas. */
export const calcQuoteTotals = (products?: QuoteProduct[] | null): QuoteTotals => {
  const items = products ?? [];

  const subtotal = round2(items.reduce((acc, product) => acc + lineSubtotal(product), 0));
  const iva = round2(items.reduce((acc, product) => acc + lineIva(product), 0));

  return { subtotal, iva, total: round2(subtotal + iva) };
};

/**
 * Totales para mostrar: usa las líneas cuando existen y, si la cotización no
 * tiene productos, cae a los valores guardados para no mostrar $0.00.
 */
export const resolveQuoteTotals = (quote: {
  products?: QuoteProduct[] | null;
  total_quote?: number | string;
  iva_quote?: number | string;
}): QuoteTotals => {
  if (quote.products && quote.products.length > 0) return calcQuoteTotals(quote.products);

  const iva = round2(toNumber(quote.iva_quote));
  const total = round2(toNumber(quote.total_quote));
  return { subtotal: round2(total - iva), iva, total };
};

/** ¿Los totales guardados difieren de los calculados? (tolerancia de 1 centavo) */
export const totalsAreStale = (
  quote: { total_quote?: number | string; iva_quote?: number | string },
  totals: QuoteTotals
): boolean =>
  Math.abs(toNumber(quote.total_quote) - totals.total) > 0.009 ||
  Math.abs(toNumber(quote.iva_quote) - totals.iva) > 0.009;
